<script setup lang="ts" generic="T extends TreeItem, N extends TreeItemNode<T>">
import { computed, ref, watch } from 'vue'
import useTree, { TreeItem, TreeItemNode } from '@/composables/useTree'
import { SelectItemKey } from 'vuetify/lib/util'

const props = defineProps<{
  nodes: N[]
  selectedItemId?: string
  selectable?: boolean
  openAll?: boolean
  itemProps?: SelectItemKey
}>()

const { treeRecursiveItemIds, treeItemIdsPathTo } = useTree()

const selectedItemIds = defineModel<string[]>('selected', { default: [] })
const onToggleNodeSelection = async (node: N, toValue: boolean) => {
  const changes = treeRecursiveItemIds(node)
  const current = [...selectedItemIds.value]
  if (toValue) {
    selectedItemIds.value = [...current, ...changes.filter((id) => !current.includes(id))]
  } else {
    selectedItemIds.value = current.filter((id) => !changes.includes(id))
  }
}

const activatedItemIdModel = defineModel<string | undefined>('activated', { default: undefined })
const activatedItemIds = ref<string[]>([])
watch(activatedItemIds, (v) => {
  activatedItemIdModel.value = v.length > 0 ? v[0] : undefined
})
watch(
  activatedItemIdModel,
  (v) => {
    activatedItemIds.value = v ? [v] : []
  },
  { immediate: true },
)

const openedItemIds = defineModel<string[]>('opened', { default: [] })
const itemIdPathToActivated = computed(() =>
  activatedItemIdModel.value
    ? treeItemIdsPathTo(props.nodes, activatedItemIdModel.value)
    : undefined,
)
watch(itemIdPathToActivated, (v) => {
  if (v) {
    openedItemIds.value.push(...v.slice(1))
  }
})

const onDoubleClick = (node: N) => {
  if (node.children.length == 0) {
    return
  }

  const id = node.item.id
  if (openedItemIds.value.includes(id)) {
    openedItemIds.value = openedItemIds.value.filter((o) => o != id)
  } else {
    openedItemIds.value = [...openedItemIds.value, id]
  }
}
</script>

<template>
  <v-treeview
    v-model:opened="openedItemIds"
    v-model:activated="activatedItemIds"
    v-model:selected="selectedItemIds"
    :items="props.nodes"
    :item-children="(node) => (node.children.length > 0 ? node.children : undefined)"
    :item-value="(node) => node.item.id"
    :item-title="(node) => node.item.id"
    :item-props="props.itemProps"
    :open-all="props.openAll"
    activatable
    active-strategy="single-independent"
    select-strategy="independent"
    density="compact"
    :open-on-click="false"
  >
    <template #prepend="{ item, isOpen, isSelected, select }">
      <v-checkbox
        v-if="props.selectable"
        density="compact"
        hide-details
        :model-value="isSelected"
        @update:model-value="(v) => select(!!v)"
        @click.shift.exact.stop="onToggleNodeSelection(item, !isSelected)"
      />
      <div @dblclick="onDoubleClick(item)">
        <slot name="prepend" :item="item" :is-open="isOpen" :is-selected="isSelected" />
      </div>
    </template>
    <template #title="{ item }">
      <div @dblclick="onDoubleClick(item)">
        <slot name="title" :item="item" />
      </div>
    </template>
    <template #subtitle="{ item }">
      <div @dblclick="onDoubleClick(item)">
        <slot name="subtitle" :item="item" />
      </div>
    </template>
    <template #append="{ item }">
      <slot name="append" :item="item" />
    </template>
  </v-treeview>
</template>

<style scoped lang="scss"></style>
